import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import $ from "jquery"

// Expects data-markers-value:
// [ { coords[42.1, -71.0], color: "#f00", text: "Hello", number: 1, route: "Route A" }, ... ]
// number is optional; when present it's shown inside the pin as-is (string or number).
// name is optional; when present it's shown as a label beside the pin when zoomed in.
// color is optional; defaults to DEFAULT_PIN_COLOR. Pin text color (black or white) is
// picked per-marker from the background's luminance so it stays readable at 4.5:1+
// regardless of how light or dark the passed-in color is.
// route is optional; when two or more distinct routes are present, a checkbox toggle
// is shown per route to show/hide its markers, and the map refits to whatever's visible.
// striped is optional; when true the pin is drawn with a hatched pattern over the color
// instead of a flat fill, so a recycled color (route 13+) still reads as distinct.
//
// data-leaflet-map-manage-colors-url-value is optional; when present, a "Manage Colors"
// link to it is appended to the bottom of the route toggle dropdown.
//
// data-leaflet-map-route-lines-value is optional: driven road routes, drawn instead of the
// straight sequence arrows:
// [ { coords: [[lat, lng], ...], color: "#911EB4", label: "Mapbox · 10:42 AM", dashed: false }, ... ]
// color, label and dashed are optional. With two or more lines, a legend toggles each one.
//
// data-leaflet-map-expandable-value="true" adds a button that grows the map to the full
// viewport height below the navbar, and back.

const DEFAULT_PIN_COLOR = '#C2410C' // bright orange, ~5.2:1 contrast with white text

// WCAG relative luminance; used to pick readable (black/white) text per pin color.
function relativeLuminance(hex) {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
  const linear = c => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
}

function textColorFor(hex) {
  // Contrast against black vs. white per WCAG; pick whichever reads better.
  const luminance = relativeLuminance(hex)
  const contrastWithBlack = (luminance + 0.05) / 0.05
  const contrastWithWhite = 1.05 / (luminance + 0.05)
  return contrastWithBlack >= contrastWithWhite ? '#000' : '#fff'
}

// Pins are MAX_PIN_SCALE at MAX_PIN_SCALE_ZOOM and closer, shrinking linearly as the map
// zooms out until they reach MIN_PIN_SCALE at MIN_PIN_SCALE_ZOOM and beyond.
const MAX_PIN_SCALE_ZOOM = 10
const MIN_PIN_SCALE_ZOOM = 7
const MAX_PIN_SCALE = 0.8
const MIN_PIN_SCALE = 0.5

// Markers with a name get a permanent label beside the pin, shown only at PIN_LABEL_ZOOM
// and closer so labels don't pile up when zoomed out.
const PIN_LABEL_ZOOM = 12

function pinScaleFor(zoom) {
  const progress = (MAX_PIN_SCALE_ZOOM - zoom) / (MAX_PIN_SCALE_ZOOM - MIN_PIN_SCALE_ZOOM)
  return MAX_PIN_SCALE - Math.min(1, Math.max(0, progress)) * (MAX_PIN_SCALE - MIN_PIN_SCALE)
}

// Purple sits outside the OpenStreetMap default palette (orange/yellow roads,
// blue water, green parks, tan buildings), so the route line reads clearly
// as an overlay rather than blending into the basemap.
const ROUTE_ARROW_COLOR = '#911EB4'

// Screen distance between arrowheads on the road route line, kept constant across zoom levels.
const ROUTE_LINE_ARROW_SPACING_PX = 120

// Classic teardrop map-pin shape: a circular head tapering to a point at the bottom.
const PIN_PATH = 'M12 0C5.383 0 0 5.383 0 12c0 9 12 20 12 20s12-11 12-20C24 5.383 18.617 0 12 0z'

// Each striped pin needs its own <pattern> id: fill="url(#id)" resolves against the
// whole document, so a repeated id would make every striped pin copy the first one's color.
let stripePatternCount = 0

function pinFill(color, striped) {
  if (!striped) return { attr: color, defs: '' }

  const patternId = `pin-stripes-${ stripePatternCount++ }`
  const hatchColor = textColorFor(color)
  const defs = `<pattern id="${ patternId }" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
    <rect width="5" height="5" fill="${ color }"/>
    <line x1="0" y1="0" x2="0" y2="5" stroke="${ hatchColor }" stroke-opacity="0.5" stroke-width="2"/>
  </pattern>`
  return { attr: `url(#${ patternId })`, defs }
}

function pinSvg(color, label, striped) {
  const fill = pinFill(color, striped)

  return `<svg width="100%" height="100%" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
    <defs>${ fill.defs }</defs>
    <path d="${ PIN_PATH }" fill="${ fill.attr }" stroke="#000" stroke-width="1"/>
    ${ label !== '' ? `<text x="12" y="13" text-anchor="middle" dominant-baseline="central" fill="${ textColorFor(color) }" font-size="12" font-weight="bold">${ label }</text>` : '' }
  </svg>`
}

export default class extends Controller {
  static values = {
    markers: Array,
    zoom: { type: Number, default: 15 },
    showArrows: { type: Boolean, default: false },
    routeLines: { type: Array, default: [] },
    manageColorsUrl: String,
    expandable: { type: Boolean, default: false }
  }

  connect() {
    this.map = L.map(this.element, { attributionControl: false })

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map)
    this.map.on('zoomend', () => this.updateZoomStyles())

    if (this.routeLinesValue.length) {
      this.addRouteLines()
    } else if (this.showArrowsValue) {
      this.addSequenceArrows()
    }
    this.addMarkers()
    this.fitBounds()
    this.updateRouteLineArrows()
    this.bindModalShown()
    if (this.expandableValue) this.addExpandControl()

    this.teardown = this.teardown.bind(this)
    document.addEventListener('turbo:before-cache', this.teardown)
  }

  updateZoomStyles() {
    const zoom = this.map.getZoom()
    this.element.style.setProperty('--pin-scale', pinScaleFor(zoom))
    this.element.classList.toggle('leaflet-show-pin-labels', zoom >= PIN_LABEL_ZOOM)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.teardown)
    this.teardown()
  }

  // Turbo snapshots the page for its cache before disconnect() runs, so Leaflet's DOM and the
  // route toggle dropdown (which lives outside this element) are also removed on
  // turbo:before-cache. Otherwise a restored snapshot shows a second, dead dropdown and a
  // new map is initialized on top of the stale one.
  teardown() {
    if (!this.map) return

    if (this.modalElement) $(this.modalElement).off('shown.bs.modal', this.handleModalShown)
    this.routeTogglesDropdown?.remove()
    this.routeTogglesDropdown = null
    this.map.remove()
    this.map = null
  }

  // Maps initialized inside a hidden Bootstrap modal render with the wrong
  // size until the modal is actually shown; recalculate once it is.
  bindModalShown() {
    this.modalElement = this.element.closest('.modal')
    if (!this.modalElement) return

    this.handleModalShown = () => {
      this.map.invalidateSize()
      this.fitBounds()
    }
    $(this.modalElement).on('shown.bs.modal', this.handleModalShown)
  }

  addExpandControl() {
    const ExpandControl = L.Control.extend({
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar')
        const button = L.DomUtil.create('a', 'leaflet-map-expand-button', container)
        button.href = '#'
        button.setAttribute('role', 'button')
        button.title = 'Full height'
        button.innerHTML = '<span class="fa fa-expand"></span>'
        L.DomEvent.disableClickPropagation(container)
        L.DomEvent.on(button, 'click', event => {
          L.DomEvent.preventDefault(event)
          this.toggleExpanded(button)
        })
        return container
      }
    })
    new ExpandControl({ position: 'topright' }).addTo(this.map)
  }

  // The expanded height comes from .leaflet-map-expanded, overriding the inline height.
  toggleExpanded(button) {
    const expanded = this.element.classList.toggle('leaflet-map-expanded')
    button.title = expanded ? 'Original height' : 'Full height'
    button.innerHTML = `<span class="fa ${ expanded ? 'fa-compress' : 'fa-expand' }"></span>`
    this.map.invalidateSize()
    this.fitBounds()
    if (expanded) this.element.scrollIntoView({ block: 'start' })
  }

  fitBounds() {
    const visibleCoords = [...this.routeLayers.values()]
      .filter(route => this.map.hasLayer(route.layer))
      .flatMap(route => route.markers.map(m => m.coords))
      .concat(this.routeLinesValue.flatMap(line => line.coords))

    if (!visibleCoords.length) return

    const bounds = L.latLngBounds(visibleCoords)
    this.map.fitBounds(bounds, { padding: [20, 20], maxZoom: this.zoomValue })
  }

  addMarkers() {
    this.routeLayers = new Map() // route name (possibly undefined) -> { layer, color, striped, markers }

    this.markersValue.forEach(m => {
      const number = m.number != null ? m.number : ''
      const color = m.color || DEFAULT_PIN_COLOR
      const icon = L.divIcon({
        className: 'custom-marker-pin',
        html: pinSvg(color, number, m.striped),
        iconSize: [24, 32],
        iconAnchor: [12, 32] // anchor at the tip of the pin
      })

      if (!this.routeLayers.has(m.route)) {
        this.routeLayers.set(m.route, {
          layer: L.layerGroup(), color, striped: m.striped, addressCount: m.address_count, markers: []
        })
      }
      const route = this.routeLayers.get(m.route)
      route.markers.push(m)
      const marker = L.marker(m.coords, { icon })
        .addTo(route.layer)
        .bindPopup(m.text)

      if (m.name) {
        // Built as a text node rather than an HTML string, since names are user-entered.
        const label = document.createElement('span')
        label.textContent = m.name
        marker.bindTooltip(label, {
          permanent: true,
          direction: 'right',
          className: 'leaflet-pin-label',
          offset: [10, -16] // beside the pin's head rather than its tip
        })
      }
    })

    this.routeLayers.forEach(route => route.layer.addTo(this.map))

    if (this.routeLayers.size > 1) this.addRouteToggles()
  }

  addRouteToggles() {
    const dropdown = document.createElement('div')
    dropdown.className = 'dropdown leaflet-route-toggles-dropdown'

    const toggleButton = document.createElement('button')
    toggleButton.type = 'button'
    toggleButton.className = 'btn btn-outline-secondary btn-sm dropdown-toggle'
    toggleButton.dataset.toggle = 'dropdown'
    toggleButton.textContent = 'Routes'

    const menu = document.createElement('div')
    menu.className = 'dropdown-menu leaflet-route-toggles'
    // Clicking a checkbox shouldn't close the dropdown, since toggling multiple
    // routes in a row is the whole point.
    menu.addEventListener('click', event => event.stopPropagation())

    this.routeCheckboxes = new Map() // route name -> checkbox element

    const showAllRow = document.createElement('div')
    showAllRow.className = 'leaflet-route-toggle-row dropdown-item'

    const showAllButton = document.createElement('button')
    showAllButton.type = 'button'
    showAllButton.className = 'leaflet-route-toggle-only'
    showAllButton.textContent = 'Show All'
    showAllButton.addEventListener('click', () => this.showAllRoutes())

    showAllRow.appendChild(showAllButton)

    const showAllDivider = document.createElement('div')
    showAllDivider.className = 'dropdown-divider'

    menu.append(showAllRow, showAllDivider)

    const routeNames = [...this.routeLayers.keys()].sort((a, b) => a.localeCompare(b))
    routeNames.forEach(routeName => {
      const route = this.routeLayers.get(routeName)

      const row = document.createElement('div')
      row.className = 'leaflet-route-toggle-row dropdown-item'

      const label = document.createElement('label')
      label.className = 'leaflet-route-toggle'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.checked = true
      checkbox.addEventListener('change', () => this.toggleRoute(routeName, checkbox.checked))
      this.routeCheckboxes.set(routeName, checkbox)

      const swatch = document.createElement('span')
      swatch.className = route.striped ? 'leaflet-route-toggle-swatch striped' : 'leaflet-route-toggle-swatch'
      swatch.style.backgroundColor = route.color

      const count = document.createElement('span')
      count.className = 'leaflet-route-toggle-count'
      count.textContent = route.addressCount

      label.append(checkbox, swatch, count, document.createTextNode(routeName))

      const onlyButton = document.createElement('button')
      onlyButton.type = 'button'
      onlyButton.className = 'leaflet-route-toggle-only'
      onlyButton.textContent = 'only'
      onlyButton.addEventListener('click', () => this.showRouteOnly(routeName))

      row.append(label, onlyButton)
      menu.appendChild(row)
    })

    if (this.hasManageColorsUrlValue) {
      const divider = document.createElement('div')
      divider.className = 'dropdown-divider'

      const manageColorsLink = document.createElement('a')
      manageColorsLink.className = 'dropdown-item'
      manageColorsLink.href = this.manageColorsUrlValue
      manageColorsLink.textContent = 'Manage Colors'

      menu.append(divider, manageColorsLink)
    }

    dropdown.append(toggleButton, menu)
    this.routeTogglesDropdown = dropdown
    this.element.before(dropdown)
  }

  showAllRoutes() {
    this.routeLayers.forEach((route, name) => {
      route.layer.addTo(this.map)
      this.routeCheckboxes.get(name).checked = true
    })
    this.fitBounds()
  }

  showRouteOnly(routeName) {
    this.routeLayers.forEach((route, name) => {
      const visible = name === routeName
      if (visible) {
        route.layer.addTo(this.map)
      } else {
        this.map.removeLayer(route.layer)
      }
      this.routeCheckboxes.get(name).checked = visible
    })
    this.fitBounds()
  }

  toggleRoute(routeName, visible) {
    const route = this.routeLayers.get(routeName)
    if (visible) {
      route.layer.addTo(this.map)
    } else {
      this.map.removeLayer(route.layer)
    }
    this.fitBounds()
  }

  // Each line gets its own layer (polyline + arrowheads) so the legend can toggle it as a unit.
  addRouteLines() {
    this.routeLines = this.routeLinesValue.map(line => {
      const color = line.color || ROUTE_ARROW_COLOR
      const layer = L.layerGroup().addTo(this.map)
      L.polyline(line.coords, { color, weight: 4, opacity: 0.7, dashArray: line.dashed ? '8 8' : null }).addTo(layer)
      const arrows = L.layerGroup().addTo(layer)
      return { ...line, color, layer, arrows }
    })
    if (this.routeLines.length > 1) this.addRouteLineLegend()
    this.map.on('zoomend', () => this.updateRouteLineArrows())
  }

  // Labels come from the server, not user input, so they're safe to use as legend HTML.
  addRouteLineLegend() {
    const overlays = {}
    this.routeLines.forEach(line => {
      const borderStyle = line.dashed ? 'dashed' : 'solid'
      const swatch = `<span style="display: inline-block; width: 24px; margin: 0 6px 3px 2px; vertical-align: middle; border-top: 4px ${ borderStyle } ${ line.color };"></span>`
      overlays[`${ swatch }${ line.label || '' }`] = line.layer
    })
    L.control.layers(null, overlays, { collapsed: false, position: 'bottomright' }).addTo(this.map)
  }

  updateRouteLineArrows() {
    this.routeLines?.forEach(line => this.drawRouteLineArrows(line))
  }

  // Walks the line in screen pixels at the current zoom, dropping an arrowhead every
  // ROUTE_LINE_ARROW_SPACING_PX. Rebuilt on zoom so they don't pile up or spread out.
  drawRouteLineArrows(line) {
    line.arrows.clearLayers()
    const points = line.coords.map(coords => this.map.latLngToLayerPoint(coords))
    let untilNextArrow = ROUTE_LINE_ARROW_SPACING_PX / 2

    for (let i = 1; i < points.length; i++) {
      const from = points[i - 1]
      const to = points[i]
      const segmentLength = from.distanceTo(to)
      let offset = 0

      while (segmentLength - offset >= untilNextArrow) {
        offset += untilNextArrow
        const progress = offset / segmentLength
        const point = L.point(from.x + (to.x - from.x) * progress, from.y + (to.y - from.y) * progress)
        // Screen y grows downward, so this is clockwise from up, matching the ▲ glyph
        const angle = Math.atan2(to.x - from.x, from.y - to.y) * 180 / Math.PI
        this.arrowheadMarker(this.map.layerPointToLatLng(point), angle, line.color).addTo(line.arrows)
        untilNextArrow = ROUTE_LINE_ARROW_SPACING_PX
      }
      untilNextArrow -= segmentLength - offset
    }
  }

  addSequenceArrows() {
    const coords = this.markersValue.map(m => m.coords)
    if (coords.length < 2) return

    L.polyline(coords, { color: ROUTE_ARROW_COLOR, weight: 4, opacity: 0.6, dashArray: '6 6' }).addTo(this.map)

    for (let i = 0; i < coords.length - 1; i++) {
      this.addArrowhead(coords[i], coords[i + 1])
    }
  }

  addArrowhead(from, to) {
    const midpoint = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2]
    this.arrowheadMarker(midpoint, this.bearing(from, to)).addTo(this.map)
  }

  arrowheadMarker(latLng, angle, color = ROUTE_ARROW_COLOR) {
    const icon = L.divIcon({
      className: 'route-arrow-icon',
      html: `<div style="transform: rotate(${angle}deg); font-size: 18px; line-height: 18px; color: ${color};">&#9650;</div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9]
    })
    return L.marker(latLng, { icon, interactive: false, zIndexOffset: -1000 })
  }

  // Initial compass bearing (degrees clockwise from north) from one point to another.
  bearing([lat1, lng1], [lat2, lng2]) {
    const toRad = deg => deg * Math.PI / 180
    const toDeg = rad => rad * 180 / Math.PI

    const dLng = toRad(lng2 - lng1)
    const y = Math.sin(dLng) * Math.cos(toRad(lat2))
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng)
    return toDeg(Math.atan2(y, x))
  }
}
